import { api } from "@vuumly-common/common";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";
import Color from "color";
import { useQuery, useTheme, useTranslate } from "hooks";
import _ from "lodash";
import { useMemo } from "react";
import { CardProps } from "../..";
import { BaseBarChart } from "../../../Charts";
import { TextH5 } from "../../../Typography/Text";
import { BaseCard } from "../../Base.card";

export const RidePerformancePerHourOfDayBarChart = (props: CardProps) => {
  const translate = useTranslate();
  const theme = useTheme();

  const { data, isLoading } = useQuery<ChartData<"bar", Record<any, number>[]>>(
    {
      apiEndpoint: api.Endpoint.RidesPerHourOfDay,
    }
  );

  const clonedData = useMemo(() => {
    if (isLoading || !data?.datasets) {
      return;
    }
    const clonedData = _.cloneDeep(data);

    clonedData?.datasets.forEach((dataset) => {
      const { label } = dataset;

      if (label === "operator_performance") {
        if (dataset.data.length) {
          dataset.backgroundColor = dataset.data.map((data) => {
            if (data.operator_performance > 0) {
              return theme.postiveBarChartColor;
            }
            return theme.negativeBarChartColor;
          });

          dataset.hoverBackgroundColor = dataset.data.map((data) => {
            if (data.operator_performance > 0) {
              return theme.hoverPositiveBarChartColor;
            }
            return theme.hoverNegativeBarChartColor;
          });
        }
        dataset.label = translate("Your performance");
        dataset.barPercentage = 0.999;
        // dataset.categoryPercentage = 1;
      } else {
        dataset.hidden = true;
        dataset.label = "none";
      }
    });

    return clonedData;
  }, [data]);

  const options: ChartOptions<"bar"> = {
    parsing: {
      yAxisKey: "operator_performance",
      xAxisKey: "dimension",
    },
    scales: {
      x: {
        title: {
          text: "Hour of day",
          display: true,
        },
        type: "time",
        time: {
          tooltipFormat: "HH:00",
          parser: "HH",
          unit: "hour",
          displayFormats: {
            hour: "HH",
          },
        },
        grid: {
          display: true,
        },
      },
      y: {
        grace: "50%",
        title: {
          text: "Your performance",
        },
        ticks: {
          display: true,
          callback: (value) => {
            return `${value}%`;
          },
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
        },
      },
    },

    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (item: TooltipItem<"bar">) => {
            const raw = item.raw as any;
            if (raw.operator_performance > 0) {
              return `${item.dataset.label}: +${item.formattedValue}%`;
            }
            return `${item.dataset.label}: ${item.formattedValue}%`;
          },
        },
      },
      datalabels: {
        display: true,
        formatter: (item: any, _ctx) => {
          if (item.operator_performance > 0) {
            return "+" + item.operator_performance.toFixed(1) + "%";
          }
          return item.operator_performance.toFixed(1) + "%";
        },
      },
    },
  };

  return (
    <BaseCard
      isLoading={isLoading}
      header={
        <TextH5>{translate("Your ride performance by hour of day")}</TextH5>
      }
    >
      <BaseBarChart options={options} data={clonedData} />
    </BaseCard>
  );
};
